import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getTeachingOverview, courseErrorMessage } from "../../courses";

const LANGUAGE_LABEL_KEYS = {
    "de-DE": "German",
    "en-GB": "English",
    "fr-FR": "French",
};

const StatTile = ({ label, value }) => (
    <div className="rounded-lg border border-border bg-white p-4 text-center">
        <p className="text-2xl md:text-3xl font-semibold text-gray-900">{value}</p>
        <p className="mt-1 text-xs md:text-sm text-gray-600">{label}</p>
    </div>
);

// Teacher-first dashboard view: prominent entry into the teaching area plus
// a quick health check of every course (students, recent activity, scores).
const TeacherHome = () => {
    const { t } = useTranslation();
    const [overview, setOverview] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        getTeachingOverview()
            .then(setOverview)
            .catch((err) => setError(courseErrorMessage(err, t)));
    }, []);

    return (
        <div className="flex flex-col gap-4">
            {/* Entry to the teaching area */}
            <div className="rounded-lg border border-border bg-secondary p-4 md:p-6">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div>
                        <h2 className="text-lg md:text-xl font-semibold text-gray-900">
                            {t("teach.heroTitle")}
                        </h2>
                        <p className="mt-1 text-sm text-gray-600">
                            {t("teach.heroSubtitle")}
                        </p>
                    </div>
                    <Link
                        to="/teach"
                        className="shrink-0 rounded-md bg-primary px-4 py-2 font-medium text-white hover:bg-purple-600 transition-colors"
                    >
                        {t("teach.openTeaching")} →
                    </Link>
                </div>
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}

            {overview === null ? (
                <div className="h-24 animate-pulse rounded-lg bg-gray-200" />
            ) : (
                <>
                    {/* Cross-course totals */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <StatTile
                            label={t("teach.coursesTile")}
                            value={overview.totals.course_count}
                        />
                        <StatTile
                            label={t("teach.studentsTile")}
                            value={overview.totals.student_count}
                        />
                        <StatTile
                            label={t("teach.attemptsLast7Days")}
                            value={overview.totals.attempts_last_7_days}
                        />
                        <StatTile
                            label={t("teach.activeLast7Days")}
                            value={overview.totals.active_students_last_7_days}
                        />
                    </div>

                    {/* Per-course cards */}
                    {overview.courses.length === 0 ? (
                        <div className="rounded-lg border border-border bg-secondary p-6 text-center">
                            <p className="text-gray-700">{t("teach.noCourses")}</p>
                            <Link
                                to="/teach"
                                className="mt-3 inline-block rounded-md bg-primary px-4 py-2 text-white hover:bg-purple-600"
                            >
                                {t("teach.createCourse")}
                            </Link>
                        </div>
                    ) : (
                        <ul className="flex flex-col gap-3">
                            {overview.courses.map((course) => (
                                <li key={course.id}>
                                    <Link
                                        to={`/teach/${course.id}`}
                                        className="block rounded-lg border border-border bg-secondary p-4 hover:bg-gray-100 transition-colors"
                                    >
                                        <div className="flex items-center justify-between flex-wrap gap-2">
                                            <div>
                                                <span className="font-medium">{course.title}</span>
                                                <span className="ml-2 text-xs text-gray-500">
                                                    {t(LANGUAGE_LABEL_KEYS[course.language] || course.language)}
                                                </span>
                                            </div>
                                            <span className="rounded bg-purple-100 px-2 py-0.5 font-mono text-sm font-semibold tracking-widest text-purple-800">
                                                {course.join_code}
                                            </span>
                                        </div>
                                        <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-gray-600">
                                            <span>
                                                {course.student_count} {t("teach.students")}
                                            </span>
                                            <span>
                                                {course.sentence_count} {t("courses.sentences")}
                                            </span>
                                            <span>
                                                {course.attempts_last_7_days}{" "}
                                                {t("teach.attemptsThisWeek")}
                                            </span>
                                            <span>
                                                {t("courses.averageScore")}:{" "}
                                                {course.average_score ?? "–"}
                                            </span>
                                        </div>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                </>
            )}
        </div>
    );
};

export default TeacherHome;
